<template>
  <div class="route-search">
    <div class="route-element">
      <div class="road-name-direction">
        <div class="buttons ib vm">
        </div>
        <div class="road-name ib vm mr4">
          <label class="form-control-label">路線名</label>
        </div>
        <div class="direction ib vm">
          <label class="form-control-label">方向</label>
        </div>
        <div class="buttons ib vm ml1">
        </div>
      </div>
    </div>
    <div class="route-element" v-for="(route, i) in state.routes" :key="i">
      <div class="road-name-direction">
        <div class="buttons ib vm">
          <div
            class="add-button"
            v-if="i < maxLen - 1 && isLast(i)"
            @click="addNextRow(route)"
            :disabled="!isRowFilled(route)"
          >
            <i class="fas fa-plus-circle"></i>
          </div>
        </div>
        <div class="road-name ib vm mr4">
          <select
            class="custom-select"
            v-model="route.selectedRoadNameDispObj"
            @change="onRoadNameDispChange(i)"
          >
            <option :value="null"></option>
            <option
              v-for="e in route.roadNameDisps"
              :key="e.roadNameDisp"
              :value="e"
            >
              {{e.roadNameDispShort}}
            </option>
          </select>
        </div>
        <div class="direction ib vm">
          <select
            class="custom-select"
            v-model="route.selectedDirection"
            @change="onDirectionChange(i)"
          >
            <option :value="''"></option>
            <option
              v-for="e in (route.selectedRoadNameDispObj || {}).directions"
              :key="e"
              :value="e"
            >
              {{e}}
            </option>
          </select>
        </div>
        <div class="buttons ib vm ml1">
          <div class="del-button" v-if="i > 1 && isLast(i)" @click="deleteLastRow()">
            <i class="far fa-times-circle"></i>
          </div>
        </div>
      </div>
      <div class="arrow-wrap">
        <template v-if="!isLast(i)">
          <i class="arrow fas fa-angle-down"></i>
        </template>
        <template v-else>
          <i class="arrow arrow-bulk fas fa-caret-down"></i>
          <i class="arrow arrow-bulk fas fa-caret-down"></i>
          <i class="arrow arrow-bulk fas fa-caret-down"></i>
        </template>
      </div>
    </div>
    <div class="ta-center mt16 mb16">
      <button
        class="btn btn-primary"
        :disabled="!canRouteSearch"
        @click="doRouteSearch"
      >
        ルート検索
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RoadNameDisp } from 'src/models/master';
import {
  computed,
  onMounted,
  reactive,
  watch,
} from 'vue';
import type { GeoConnection } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import type { RouteSearchRouteEntry } from 'src/models/apis/movie/movieRequest';
import { isKpIncreaseDirection } from 'src/lib/kilopostHelper';
import { useReactiveMasters } from 'src/composables/useMasters';

interface RouteInfoItem {
  type: 'start' | 'end';
  roadNameDisp: string;
  roadName: string;
  direction: string;
  placeName: string;
  kpEnd?: number;
  kpStart?: number;
}

interface RouteInfo {
  routeInfo: RouteInfoItem[];
  dstRoadNameDisp: string;
  dstRoadName: string;
  dstDirection: string;
}

interface GetRouteInfoParams {
  k: string;
  obj: GeoConnection;
  srcRoadName: string;
  srcDirection: string;
}

type CanMapElement = RoadNameDisp & {routeInfoMap: {[k: string]: RouteInfoItem[] }};

type LocalRoute = {
  roadNameDisps: RoadNameDisp[];
  selectedRoadNameDispObj: CanMapElement | null;
  selectedDirection: string;
};

interface RouteSearchState {
  routes: LocalRoute[];
}

interface Props {
  roadNameDisps: RoadNameDisp[];
  isValidDateParams: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits(['route-search']);

const state = reactive<RouteSearchState>({
  routes: [],
});
const { masters: msts } = useReactiveMasters();

onMounted(() => {
  if (props.roadNameDisps.length > 0 && state.routes.length === 0) {
    initData();
  }
});

watch(() => props.roadNameDisps, () => {
  if (props.roadNameDisps.length > 0 && state.routes.length === 0) {
    initData();
  }
});

// computed
const canRouteSearch = computed(() => {
  if (!props.isValidDateParams) return false;
  if (state.routes.length < 2) return false;

  for (const route of state.routes) {
    if (!isRowFilled(route)) {
      return false;
    }
  }
  return true;
});

// methods
const initData = () => {
  const arr: LocalRoute[] = [];
  arr.push(getNewRow(props.roadNameDisps));
  arr.push(getNewRow([]));
  state.routes = arr;
};
const getNewRow = (candidacies: RoadNameDisp[]): LocalRoute => ({
  roadNameDisps: candidacies,
  selectedRoadNameDispObj: null,
  selectedDirection: '',
});
const isLast = (idx: number) => {
  return idx >= state.routes.length - 1;
};
const isRowFilled = (rowObj: LocalRoute) => {
  return !!rowObj.selectedRoadNameDispObj &&
    !!rowObj.selectedDirection;
};
const getRouteInfo = ({ k, obj, srcRoadName, srcDirection }: GetRouteInfoParams): RouteInfo => {
  let [dstRoadName, dstDirection, dstPlaceName] = k.split('#');
  const routeInfo: RouteInfoItem[] = [
    {
      type: 'end',
      roadNameDisp: msts.kp.roadNameToRoadNameDispMap[srcRoadName],
      roadName: srcRoadName,
      direction: srcDirection,
      placeName: 'main_line',
      kpEnd: obj.srcKpEnd,
    },
    {
      type: 'start',
      roadNameDisp: msts.kp.roadNameToRoadNameDispMap[dstRoadName],
      roadName: dstRoadName,
      direction: dstDirection,
      placeName: dstPlaceName || 'main_line',
      kpStart: obj.dstKpStart,
    },
  ];
  if (obj.nextKey) {
    const [viaRoadName, viaDirection, viaPlaceName] =
      [dstRoadName, dstDirection, dstPlaceName];
    const tmpSplit = obj.nextKey.split('#');
    dstRoadName = tmpSplit[0];
    dstDirection = tmpSplit[1];
    dstPlaceName = tmpSplit[2];
    if (dstPlaceName) {
      // 行き先が本線以外の場合というのはないとは思うが、あったらはじく
      return {} as RouteInfo;
    }
    const nestedObj = obj.nested[obj.nextKey];
    if (!nestedObj) return {} as RouteInfo;
    routeInfo.push(
      {
        type: 'end',
        roadNameDisp: msts.kp.roadNameToRoadNameDispMap[viaRoadName],
        roadName: viaRoadName,
        direction: viaDirection,
        placeName: viaPlaceName || 'main_line',
        kpEnd: nestedObj.srcKpEnd,
      },
      {
        type: 'start',
        roadNameDisp: msts.kp.roadNameToRoadNameDispMap[dstRoadName],
        roadName: dstRoadName,
        direction: dstDirection,
        placeName: dstPlaceName || 'main_line',
        kpStart: nestedObj.dstKpStart,
      },
    );
  }
  const dstRoadNameDisp = msts.kp.roadNameToRoadNameDispMap[dstRoadName];
  return { routeInfo, dstRoadNameDisp, dstRoadName, dstDirection };
};
const getNextCandidacies = (rowObj: LocalRoute) => {
  if (!isRowFilled(rowObj) || rowObj.selectedRoadNameDispObj === null) return [];

  // roadNameDispに含まれるroadNameごとに処理を行う
  const srcRoadNameDisp = rowObj.selectedRoadNameDispObj.roadNameDisp;
  const srcDirection = rowObj.selectedDirection;

  const candMap: { [k: string]: CanMapElement } = {};
  const srcRoadNames = msts.kp.roadNameDispMap[srcRoadNameDisp].roadNames;
  srcRoadNames.forEach(srcRoadName => {
    const connection = msts.kp.geoConnections[`${srcRoadName}#${srcDirection}`];
    if (!connection) return;

    // src情報からありうるdst情報を算出
    for (const [k, obj] of Object.entries(connection)) {
      const { routeInfo, dstRoadNameDisp, dstDirection } =
        getRouteInfo({ k, obj, srcRoadName, srcDirection });
      if (!routeInfo) { continue; }
      // 自分への接続は認めない
      if (
        srcRoadNameDisp === dstRoadNameDisp &&
        srcDirection === dstDirection
      ) { continue; }
      // dstRoadNameDispごとにまとめ直す
      // dstRoadNameDisp#dstDirectionではかぶらないはずだから、
      // それ単位であとで取れるようにrouteInfoを保存しておく
      if (!dstRoadNameDisp || !dstDirection) { continue; }
      if (!candMap[dstRoadNameDisp]) {
        candMap[dstRoadNameDisp] =
          JSON.parse(JSON.stringify(msts.kp.roadNameDispMap[dstRoadNameDisp]));
        candMap[dstRoadNameDisp].directions = [];
        candMap[dstRoadNameDisp].routeInfoMap = {};
      }
      const candMapEnt = candMap[dstRoadNameDisp];
      if (!candMapEnt.routeInfoMap[dstDirection]) {
        candMapEnt.directions.push(dstDirection);
      }
      candMapEnt.routeInfoMap[dstDirection] = routeInfo;
    }
  });
  const ret = Object.entries(candMap).reduce((acc: CanMapElement[], ent) => {
    acc.push(ent[1]); return acc;
  }, []);
  return ret;
};

const maxLen = 3;
const addNextRow = (currentRow: LocalRoute) => {
  if (state.routes.length >= maxLen || !isRowFilled(currentRow)) return;

  const cands = getNextCandidacies(currentRow);
  const row = getNewRow(cands);
  state.routes.push(row);
};
const deleteLastRow = () => {
  const idx = state.routes.length - 1;
  if (idx <= 0) return;

  state.routes.splice(idx, 1);
};
const onSelectionChange = (idx: number) => {
  // 後続の行があれば全て選択解除
  state.routes.slice(idx + 1).forEach(e => {
    e.roadNameDisps = [];
    e.selectedRoadNameDispObj = null;
    e.selectedDirection = '';
  });
  // 次の行があれば候補をセット
  if (idx >= state.routes.length - 1) return;

  const cands = getNextCandidacies(state.routes[idx]);
  state.routes[idx + 1].roadNameDisps = cands;
};
const onRoadNameDispChange = (idx: number) => {
  state.routes[idx].selectedDirection = '';
  onSelectionChange(idx);
};
const onDirectionChange = (idx: number) => {
  onSelectionChange(idx);
};
const kpBinSearch = (kps: GIKp[], num: number) => {
  // numを見つける. kpsは昇順前提.
  let low = 0;
  let high = kps.length - 1;
  let mid, guess;
  while (low <= high) {
    mid = Math.floor((low + high) / 2);
    guess = kps[mid];
    if (Math.abs(guess.kpCalc - num) < Number.EPSILON) {
      return mid;
    } else if (guess.kpCalc > num) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return -1;
};
const findFirstKpOutsideDistance = (
  roadName: string,
  direction: string,
  kpCalc0: number,
  moveIndexFunc: (_: number) => number,
  distance: number,
): GIKp | null => {
  const kpMapKey = `${roadName}#${direction}`;
  const kps = msts.kp.kpMap.get(kpMapKey)?.get('main_line');
  if (!kps) return null;

  const lastIdx = kps.length - 1;
  let idx = kpBinSearch(kps, kpCalc0);
  while (true) {
    const nextIdx = moveIndexFunc(idx);
    if (nextIdx < 0 || nextIdx > lastIdx) { break; }
    idx = nextIdx;
    const cmpKpObj = kps[idx];
    if (Math.abs(kpCalc0 - cmpKpObj.kpCalc) > distance) {
      break;
    }
  }
  return kps[idx];
};
// routesの始点側と終点側に付け足すKPの長さ
// (路線全体だとさすがに長くなりすぎるので)
const routeMarginDistance = 2.0;
const getStartKpOfRoute = (routeObj: RouteSearchRouteEntry) => {
  const isKpInc = isKpIncreaseDirection(
    routeObj.road_name,
    routeObj.direction,
  );
  const moveIndexFunc = isKpInc ? (i: number) => i - 1 : (i: number) => i + 1;
  const kpObj = findFirstKpOutsideDistance(
    routeObj.road_name,
    routeObj.direction,
    routeObj.kp_calc_end,
    moveIndexFunc,
    routeMarginDistance,
  );
  return kpObj?.kpCalc;
};
const getEndKpOfRoute = (routeObj: RouteSearchRouteEntry) => {
  const isKpInc = isKpIncreaseDirection(
    routeObj.road_name,
    routeObj.direction,
  );
  const moveIndexFunc = isKpInc ? (i: number) => i + 1 : (i: number) => i - 1;
  const kpObj = findFirstKpOutsideDistance(
    routeObj.road_name,
    routeObj.direction,
    routeObj.kp_calc_start,
    moveIndexFunc,
    routeMarginDistance,
  );
  return kpObj?.kpCalc;
};
const fillStartSideRouteInfo = (routeEntries: RouteSearchRouteEntry[]) => {
  // routesの先頭要素にkp_calc_startが入ってないので入れる.
  // 場合によっては先頭側に新たな要素を追加する必要もあるが、
  // (roadNameの前側に同じroadNameDispで異なるroadNameがあるとか)
  // 今は省略する.
  const firstRoute = routeEntries[0];
  firstRoute.kp_calc_start = getStartKpOfRoute(firstRoute) || 0;
};
const fillEndSideRouteInto = (routeEntries: RouteSearchRouteEntry[]) => {
  // routesの末尾要素にkp_calc_endが入ってないので入れる.
  // あと、fillStartSideRouteInfoと同様に省略.
  const lastRoute = routeEntries[routeEntries.length - 1];
  lastRoute.kp_calc_end = getEndKpOfRoute(lastRoute) || 0;
};
const doRouteSearch = () => {
  const routeEntries: RouteSearchRouteEntry[] = [];
  let currentRouteBlock: RouteSearchRouteEntry | null = null;
  for (const route of state.routes) {
    if (!isRowFilled(route)) return;

    const routeInfoMap = route.selectedRoadNameDispObj?.routeInfoMap;
    // 最初と最後はあとで詰める
    if (!routeInfoMap) { continue; }

    const routeInfo = routeInfoMap[route.selectedDirection];
    routeInfo.forEach(e => {
      if (!currentRouteBlock) {
        // 初回
        currentRouteBlock = {
          road_name: e.roadName,
          direction: e.direction,
          place_name: e.placeName,
          kp_calc_start: 0, // dummy
          kp_calc_end: 0, // dummy
        };
      }
      if (e.type === 'start') {
        currentRouteBlock = {
          road_name: e.roadName,
          direction: e.direction,
          place_name: e.placeName,
          kp_calc_start: e.kpStart || 0,
          kp_calc_end: 0, // dummy
        };
      } else {
        currentRouteBlock.kp_calc_end = e.kpEnd || 0;
        routeEntries.push(currentRouteBlock);
      }
    });
  }
  if (currentRouteBlock) {
    routeEntries.push(currentRouteBlock);
  }

  // 先頭側のルート情報を追加
  fillStartSideRouteInfo(routeEntries);
  // 末尾のルート情報を追加
  fillEndSideRouteInto(routeEntries);

  emit('route-search', { data: routeEntries });
};
</script>

<style lang="scss" scoped>
</style>
